import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Mail } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { apiJson, ApiError } from "@/utils/fetch";
import { getErrorMessage } from "@/utils/errors";
import { supabase } from "@/utils/supabase";
import type { InvitationByToken } from "@/types";

export function InvitationAcceptPage() {
  const navigate = useNavigate();
  const [token, setToken] = useState<string | null>(null);
  const [invite, setInvite] = useState<InvitationByToken | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [hasSession, setHasSession] = useState<boolean | null>(null);

  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const t = sp.get("token");
    setToken(t);
    if (!t) {
      setError("Missing token.");
      return;
    }
    void supabase.auth.getSession().then(({ data }) => {
      setHasSession(Boolean(data.session));
    });
    // Public lookup of the invitation (no auth required).
    (async () => {
      try {
        const r = await fetch(`/api/v1/invitations/by-token/${encodeURIComponent(t)}`);
        if (!r.ok) throw new Error(await r.text());
        setInvite((await r.json()) as InvitationByToken);
      } catch (e) {
        setError(getErrorMessage(e));
      }
    })();
  }, []);

  async function accept() {
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      const res = await apiJson<{ org_id: string }>(
        `/invitations/by-token/${encodeURIComponent(token)}/accept`,
        { method: "POST", body: JSON.stringify({}) },
      );
      navigate({ to: "/orgs/$orgId", params: { orgId: res.org_id } });
    } catch (e) {
      const msg = e instanceof ApiError ? e.body : getErrorMessage(e);
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  if (error) {
    return (
      <Center>
        <p className="text-sm text-destructive">{error}</p>
      </Center>
    );
  }
  if (!invite) {
    return (
      <Center>
        <p className="text-sm text-muted-foreground">Loading invitation…</p>
      </Center>
    );
  }

  return (
    <Center>
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Mail className="size-5" />
            </div>
            <div>
              <CardTitle>Join {invite.org_name}</CardTitle>
              <CardDescription>
                Invited as {invite.role} ({invite.invitee_email})
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {invite.status !== "pending" ? (
            <p className="text-sm text-destructive">
              This invitation has been {invite.status}.
            </p>
          ) : hasSession ? (
            <Button onClick={accept} disabled={busy} className="w-full">
              {busy ? "Accepting…" : "Accept invitation"}
            </Button>
          ) : (
            <Button
              className="w-full"
              onClick={() => {
                const next = `/invitations/accept?token=${encodeURIComponent(token!)}`;
                navigate({ to: "/log-in", search: { next } });
              }}
            >
              Sign in to accept
            </Button>
          )}
        </CardContent>
      </Card>
    </Center>
  );
}

function Center({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-12">
      {children}
    </div>
  );
}
